import {HeldAwayAccount, HeldAwayAccountSummary, LegalAgreement} from './models/InvestmentProgram';
import {
    LegalEntityReadModel,
    LegalEntityType,
    legalEntityTypes,
    MemberOwnership,
    OwnershipReadModel
} from "./models/Ownership";
import {LifeInsurance} from "./models/LifeInsurance";
import {GroupedTableData, GroupedTableItem} from "../components/Table/GroupedTable";
import {AssetsSummary} from "./models/Assets";
import {PersonalAsset} from "./models/PersonalAsset";
import moment from "moment/moment";
import {DISPLAY_DATE_FORMAT} from "../constants/common";
import {StandaloneAccount} from "./models/StandaloneAccount";
import {Member} from "../ClientManagement/models/InvestorGroupType";
import {AssetsViewContextValue} from "./common/AssetsViewContext";
import {PersonalLiability} from "./models/PersonalLiability";
import {PartiallyOwnedLegalAgreement} from "./models/PartiallyOwnedInvestmentAccount";
import {BankingAccountsSummary} from "./models/BankingAccounts";


type MemberID = string;

export type LegalEntityTypeSummary = {
    entityType: LegalEntityType,
    totalValue: number,
    entities: GroupedTableData[]
};

export type NonClientMemberSummary = {
    memberName: string,
    totalValue: number,
    assets: GroupedTableData[]
};

interface NonClientMemberAsset {
    profileId: string,
    assetId: string,
    assetName: string,
    assetDetails?: string
    assetType: 'StandaloneAccount' | 'PersonalAsset' | 'LifeInsurance' | 'LegalAgreement' | 'PersonalLiability' | 'PartiallyOwnedInvestment' | 'HeldAwayAccount' | 'BankingAccount',
    value: number,
    member: Member
}

export type HeldAwayAndBankingDetails = {
    heldAwayAccountDetails?: (account: HeldAwayAccount) => string,
    bankingAccountDetails?: (account: BankingAccountsSummary) => string
}


export function createLegalEntityTypeSummariesWithBankingDetails(
    viewType: AssetsViewContextValue,
    assets: AssetsSummary,
    legalEntities: LegalEntityReadModel[],
    getPersonalAssetDetails?: (asset: PersonalAsset) => string | undefined,
    getStandaloneAccountDetails?: (account: StandaloneAccount) => string,
    useDeathBenefitForLifeInsurance: boolean = false,
    heldAwayAndBankingDetails?: HeldAwayAndBankingDetails

): Array<LegalEntityTypeSummary> {
    const legalEntityTypeSummaries = legalEntities.reduce((resultingSummaries: LegalEntityTypeSummary[], legalEntity) => {
        let typeSummary = resultingSummaries.find(summary => summary.entityType === legalEntity.type);
        if (!typeSummary) {
            typeSummary = {entityType: legalEntity.type, totalValue: 0, entities: []};
            resultingSummaries.push(typeSummary);
        }
        const allAssetsForEntity = getAllAssetsForEntityWithBankingDetails(viewType, assets, legalEntity, getPersonalAssetDetails, getStandaloneAccountDetails, useDeathBenefitForLifeInsurance,heldAwayAndBankingDetails);
        typeSummary.entities.push({groupName: legalEntity.name, groupId: legalEntity.id, items: allAssetsForEntity});
        typeSummary.totalValue += allAssetsForEntity.reduce((total, groupedItem) => total + groupedItem.itemValue, 0)
        return resultingSummaries;
    }, []);

    return legalEntityTypeSummaries.sort(byCustomOrder);
}

export function createNonClientMemberSummaries(
    viewType: AssetsViewContextValue,
    assetsSummary: AssetsSummary,
    getPersonalAssetDetailsFn?: (asset: PersonalAsset) => string,
    getStandaloneAccountDetailsFn?: (asset: StandaloneAccount) => string,
    useDeathBenefitForLifeInsurance: boolean = false,
    getHeldAwayAccountDetailsFn?: (asset: HeldAwayAccount) => string,
    getHBankingAccountDetailsFn?: (asset: BankingAccountsSummary) => string
): NonClientMemberSummary[] {
    const groupedSummaries = new Map<MemberID, NonClientMemberSummary>();
    const nonClientMemberAssets = mapToNonClientMemberAssets(assetsSummary, getPersonalAssetDetailsFn, getStandaloneAccountDetailsFn, useDeathBenefitForLifeInsurance,getHeldAwayAccountDetailsFn,getHBankingAccountDetailsFn);

    for (const memberAsset of nonClientMemberAssets) {
        const memberId = memberAsset.member?.id;
        let memberSummary = memberId ? groupedSummaries.get(memberId) : undefined;
        if (!memberSummary) {
            const initialMemberSummary = {
                memberName: memberAsset.member.name,
                totalValue: 0,
                assets: [{groupId: memberId, items: []}]
            };
            groupedSummaries.set(memberId, initialMemberSummary);
            memberSummary = initialMemberSummary;
        }

        memberSummary.totalValue += memberAsset.value;
        memberSummary.assets[0].items.push({
            itemName: memberAsset.assetName,
            itemNameSubtext: memberAsset.assetDetails,
            itemValue: memberAsset.value,
            menuOptions: getMenuOptions(viewType, memberAsset)
        });
    }

    return Array.from(groupedSummaries.values());
}

function mapToNonClientMemberAssets(
    assetsSummary: AssetsSummary,
    getPersonalAssetDetailsFn?: (asset: PersonalAsset) => string,
    getStandaloneAccountDetailsFn?: (asset: StandaloneAccount) => string,
    useDeathBenefitForLifeInsurance: boolean = false,
    getHeldAwayAccountDetailsFn? : (asset: HeldAwayAccount) => string,
    getBankingAccountDetailsFn? : (asset: BankingAccountsSummary) => string
): NonClientMemberAsset[] {
    const personalAssets = assetsSummary.personalAssets.data.flatMap((asset) => {
        return getNonClientMemberOwnerships(asset)
            .map(ownership => mapPersonalAssetToNonClientMemberAsset(ownership, asset, getPersonalAssetDetailsFn));
    });

    const legalAgreements = assetsSummary.investmentProgram
        ? assetsSummary.investmentProgram.legalAgreements.flatMap(asset => {
            return getNonClientMemberOwnerships(asset)
                .map(ownership => mapLegalAgreementToNonClientMemberAsset(ownership, asset))
        })
        : [];

    const partiallyOwnedInvestmentAccounts = assetsSummary.partiallyOwnedLegalAgreements.flatMap(asset => {
            return getNonClientMemberOwnerships(asset)
                .map(ownership => mapPartiallyOwnedInvestmentAccountToNonClientMemberAsset(ownership, asset))
        });

    const standaloneAccounts = assetsSummary.accounts.data.flatMap((asset) => {
        return getNonClientMemberOwnerships(asset)
            .map(ownership => mapStandaloneAccountToNonClientMemberAsset(ownership, asset, getStandaloneAccountDetailsFn));
    });

    const heldAwayAccounts = assetsSummary.investmentProgram
        ? assetsSummary.investmentProgram.heldAwayAccounts.flatMap((asset) => {
        return getNonClientMemberOwnerships(asset)
            .map(ownership => mapHeldAwayAccountToNonClientMemberAsset(ownership, asset, getHeldAwayAccountDetailsFn));
    })
        : [];


    const bankingAccounts = assetsSummary.bankingAccounts
        ? assetsSummary.bankingAccounts.flatMap((asset) => {
            return getNonClientMemberOwnerships(asset)
                .map(ownership => mapBankingAccountToNonClientMemberAsset(ownership, asset, getBankingAccountDetailsFn));
        })
        : [];


    const lifeInsurances = assetsSummary.lifeInsurances.data.flatMap(asset => {
        return getNonClientMemberOwnerships(asset)
            .map(ownership => mapLifeInsuranceToNonClientMemberAsset(ownership, asset, useDeathBenefitForLifeInsurance))
    });

    const personalLiabilities = assetsSummary.personalLiabilities.flatMap((liability) => {
        return getNonClientMemberOwnerships(liability)
            .map(ownership => mapPersonalLiabilityToNonClientMemberAsset(ownership, liability));
    });

    return [
        ...personalAssets,
        ...legalAgreements,
        ...partiallyOwnedInvestmentAccounts,
        ...standaloneAccounts,
        ...heldAwayAccounts,
        ...bankingAccounts,
        ...lifeInsurances,
        ...personalLiabilities

    ];
}

function mapPersonalAssetToNonClientMemberAsset(
    ownership: MemberOwnership,
    asset: PersonalAsset,
    getAssetDetailsFn: (asset: PersonalAsset) => string | undefined = () => undefined
): NonClientMemberAsset {
    return {
        profileId: asset.profileId,
        assetId: asset.id,
        assetName: asset.description,
        assetDetails: getAssetDetailsFn(asset),
        assetType: 'PersonalAsset',
        value: calculateValue(asset.presentValue, ownership),
        member: ownership.member
    };
}

function mapStandaloneAccountToNonClientMemberAsset(
    ownership: MemberOwnership,
    asset: StandaloneAccount,
    getAssetDetailsFn: (asset: StandaloneAccount) => string | undefined = () => formatAsOfDate(asset.asOfDate)
): NonClientMemberAsset {
    return {
        profileId: asset.profileId,
        assetId: asset.id,
        assetName: asset.name,
        assetDetails: getAssetDetailsFn(asset),
        assetType: 'StandaloneAccount',
        value: calculateValue(asset.holdings.totalMarketValue, ownership),
        member: ownership.member
    };
}

function mapHeldAwayAccountToNonClientMemberAsset(
    ownership: MemberOwnership,
    asset: HeldAwayAccountSummary,
    getAssetDetailsFn: (asset: HeldAwayAccountSummary) => string | undefined = () => formatAsOfDate(asset.holdings.asOfDate)
): NonClientMemberAsset {
    return {
        profileId: asset.profileId,
        assetId: asset.id,
        assetName: asset.financialAccountName,
        assetDetails: getAssetDetailsFn(asset),
        assetType: 'HeldAwayAccount',
        value: calculateValue(asset.marketEstateValue.totalValue, ownership),
        member: ownership.member
    };
}




function mapBankingAccountToNonClientMemberAsset(
    ownership: MemberOwnership,
    asset: BankingAccountsSummary,
    getAssetDetailsFn: (asset: BankingAccountsSummary) => string | undefined = () => formatAsOfDate(asset.valueAsOfDate)
): NonClientMemberAsset {
    return {
        profileId: asset.profileId,
        assetId: asset.id,
        assetName: asset.accountName,
        assetDetails: getAssetDetailsFn(asset),
        assetType: 'BankingAccount',
        value: calculateValue(asset.eodNtBalance, ownership),
        member: ownership.member
    };
}




function mapLifeInsuranceToNonClientMemberAsset(
    ownership: MemberOwnership,
    asset: LifeInsurance,
    useDeathBenefitForLifeInsurance: boolean
): NonClientMemberAsset {

    const assetValue = useDeathBenefitForLifeInsurance ? asset.deathBenefitValue : asset.cashValue;

    return {
        profileId: asset.profileId,
        assetId: asset.id,
        assetName: asset.description,
        assetType: 'LifeInsurance',
        value: calculateValue(assetValue, ownership),
        member: ownership.member
    };
}

function mapLegalAgreementToNonClientMemberAsset(
    ownership: MemberOwnership,
    asset: LegalAgreement
): NonClientMemberAsset {

    return {
        profileId: asset.profileId,
        assetId: asset.id,
        assetName: asset.name,
        assetType: 'LegalAgreement',
        value: calculateValue(asset.marketValue, ownership),
        member: ownership.member
    };
}

function mapPartiallyOwnedInvestmentAccountToNonClientMemberAsset(
    ownership: MemberOwnership,
    asset: PartiallyOwnedLegalAgreement
): NonClientMemberAsset {

    return {
        profileId: asset.profileId,
        assetId: asset.id,
        assetName: asset.legalAgreementName,
        assetType: 'PartiallyOwnedInvestment',
        value: calculateValue(asset.marketEstateValue.totalValue, ownership),
        member: ownership.member
    };
}

function mapPersonalLiabilityToNonClientMemberAsset(
    ownership: MemberOwnership,
    liability: PersonalLiability
): NonClientMemberAsset {
    const loanBalance = -1 * liability.loanBalanceEstateValue.totalValue;

    return {
        profileId: liability.profileId,
        assetId: liability.id,
        assetName: liability.description,
        assetType: 'PersonalLiability',
        value: calculateValue(loanBalance, ownership),
        member: ownership.member
    };
}

function getNonClientMemberOwnerships(asset: OwnershipReadModel) {
    return asset.memberOwnerships.filter(ownership => !ownership.isInEstateMember);
}

function getAllAssetsForEntityWithBankingDetails(
    viewType: AssetsViewContextValue,
    assetsSummary: AssetsSummary,
    legalEntity: LegalEntityReadModel,
    getPersonalAssetDetails?: (asset: PersonalAsset) => string | undefined,
    getStandaloneAccountDetails?: (account: StandaloneAccount) => string,
    useDeathBenefitForLifeInsurance: boolean = false,
    heldAwayAndBankingDetails?: HeldAwayAndBankingDetails
): Array<GroupedTableItem> {
    const personalEntityAssets = assetsSummary.personalAssets.data
        .filter(asset => isOwnedByEntity(asset, legalEntity.id))
        .map(asset => mapPersonalAssetToGroupTableItem(viewType, asset, legalEntity, getPersonalAssetDetails));

    const legalAgreementEntityAssets = assetsSummary.investmentProgram
        ? assetsSummary.investmentProgram.legalAgreements
            .filter(asset => isOwnedByEntity(asset, legalEntity.id))
            .map(asset => mapLegalAgreementToGroupTableItem(viewType, asset, legalEntity))
        : [];

    const partiallyOwnedInvestmentAssets = assetsSummary.partiallyOwnedLegalAgreements
        .filter(asset => isOwnedByEntity(asset, legalEntity.id))
        .map(asset => mapPartiallyOwnedInvestmentToGroupTableItem(viewType, asset, legalEntity));

    const accountEntityAssets = assetsSummary.accounts.data
        .filter(asset => isOwnedByEntity(asset, legalEntity.id))
        .map(asset => mapStandaloneAccountToGroupTableItem(viewType, asset, legalEntity, getStandaloneAccountDetails));

    const heldAwayAccountEntityAssets = assetsSummary.investmentProgram
        ? assetsSummary.investmentProgram.heldAwayAccounts
        .filter(asset => isOwnedByEntity(asset, legalEntity.id))
        .map(asset => mapHeldAwayAccountToGroupTableItem(viewType, asset, legalEntity, heldAwayAndBankingDetails?.heldAwayAccountDetails))
        : [];


    const bankingAccountEntityAssets = assetsSummary.bankingAccounts
        ? assetsSummary.bankingAccounts
            .filter(asset => isOwnedByEntity(asset, legalEntity.id))
            .map(asset => mapBankingAccountToGroupTableItem(viewType, asset, legalEntity, heldAwayAndBankingDetails?.bankingAccountDetails))
        : [];

    const lifeInsuranceEntityAssets = assetsSummary.lifeInsurances.data
        .filter(asset => isOwnedByEntity(asset, legalEntity.id))
        .map(asset => mapLifeInsuranceToGroupTableItem(viewType, asset, legalEntity, useDeathBenefitForLifeInsurance));

    const personalLiabilityEntityAssets = assetsSummary.personalLiabilities
        .filter(asset => isOwnedByEntity(asset, legalEntity.id))
        .map(liability => mapPersonalLiabilityToGroupTableItem(viewType, liability, legalEntity));

    return [
        ...legalAgreementEntityAssets,
        ...partiallyOwnedInvestmentAssets,
        ...accountEntityAssets,
        ...heldAwayAccountEntityAssets,
        ...bankingAccountEntityAssets,
        ...personalEntityAssets,
        ...lifeInsuranceEntityAssets,
        ...personalLiabilityEntityAssets
    ];
}

function isOwnedByEntity(asset: OwnershipReadModel, entityId: string): boolean {
    return asset.legalEntityOwnerships.some(ownership => ownership.legalEntity.id === entityId);
}

function mapPersonalAssetToGroupTableItem(
    viewType: AssetsViewContextValue,
    asset: PersonalAsset,
    entity: LegalEntityReadModel,
    getItemNameSubText: (asset: PersonalAsset) => string | undefined = () => undefined,
): GroupedTableItem {

    const entityOwnership = asset.legalEntityOwnerships.find(ownership => ownership.legalEntity.id === entity.id)!;
    const menuOptions = [{
        itemText: 'Edit Asset',
        value: asset.id,
        route: `/Profile/${asset.profileId}/ClientProfile/${viewType}/EditPersonalAsset/${asset.id}`,
    }];
    return {
        itemName: asset.description,
        itemValue: calculateValue(asset.presentValue, entityOwnership),
        itemNameSubtext: getItemNameSubText(asset),
        menuOptions
    }
}

function mapStandaloneAccountToGroupTableItem(
    viewType: AssetsViewContextValue,
    asset: StandaloneAccount,
    entity: LegalEntityReadModel,
    getItemNameSubText: (account: StandaloneAccount) => string = (account) => formatAsOfDate(account.asOfDate)
): GroupedTableItem {

    const entityOwnership = asset.legalEntityOwnerships.find(ownership => ownership.legalEntity.id === entity.id)!;
    const menuRouteType = 'EditStandaloneAccount';
    const menuOptions = [{
        itemText: 'Edit Account',
        value: asset.id,
        route: `/Profile/${asset.profileId}/ClientProfile/${viewType}/${menuRouteType}/${asset.id}`,
    }];
    return {
        itemName: asset.name,
        itemNameSubtext: getItemNameSubText(asset),
        itemValue: ((asset.holdings.totalMarketValue ? asset.holdings.totalMarketValue : 0) * entityOwnership.percentage) / 100,
        menuOptions
    };
}

function mapLifeInsuranceToGroupTableItem(viewType: AssetsViewContextValue,
                                          asset: LifeInsurance,
                                          entity: LegalEntityReadModel,
                                          useDeathBenefitForLifeInsurance = false): GroupedTableItem {

    const entityOwnership = asset.legalEntityOwnerships.find(ownership => ownership.legalEntity.id === entity.id)!;
    const menuOptions = [{
        itemText: 'Edit Asset',
        value: asset.id,
        route: `/Profile/${asset.profileId}/ClientProfile/${viewType}/EditLifeInsurance/${asset.id}`,
    }];

    const assetValue = useDeathBenefitForLifeInsurance ? asset.deathBenefitValue : asset.cashValue;

    return {
        itemName: asset.description,
        itemValue: calculateValue(assetValue, entityOwnership),
        menuOptions
    };
}

function mapPersonalLiabilityToGroupTableItem(viewType: AssetsViewContextValue,
                                          liability: PersonalLiability,
                                          entity: LegalEntityReadModel): GroupedTableItem {

    const entityOwnership = liability.legalEntityOwnerships.find(ownership => ownership.legalEntity.id === entity.id)!;
    const menuOptions = [{
        itemText: 'Edit Liability',
        value: liability.id,
        route: `/Profile/${liability.profileId}/ClientProfile/${viewType}/EditPersonalLiability/${liability.id}`,
    }];

    const loanBalance = -1 * liability.loanBalanceEstateValue.totalValue;

    return {
        itemName: liability.description,
        itemValue: calculateValue(loanBalance, entityOwnership),
        menuOptions
    };
}

function mapLegalAgreementToGroupTableItem(viewType: AssetsViewContextValue,
                                          asset: LegalAgreement,
                                          entity: LegalEntityReadModel): GroupedTableItem {

    const entityOwnership = asset.legalEntityOwnerships.find(ownership => ownership.legalEntity.id === entity.id)!;
    const menuOptions = [
        {
        itemText: 'Edit Account',
        value: 'Edit Account',
        route: `/Profile/${asset.profileId}/ClientProfile/${viewType}/EditLegalAgreement/${asset.id}`
    }];

    return {
        itemName: asset.name,
        itemValue: calculateValue(asset.marketValue, entityOwnership),
        menuOptions
    };
}

function mapHeldAwayAccountToGroupTableItem(
    viewType: AssetsViewContextValue,
    asset: HeldAwayAccountSummary,
    entity: LegalEntityReadModel,
    getItemNameSubText: (heldAwayAccount: HeldAwayAccountSummary) => string = (heldAwayAccount) => formatAsOfDate(heldAwayAccount.holdings.asOfDate)
): GroupedTableItem {

    const entityOwnership = asset.legalEntityOwnerships.find(ownership => ownership.legalEntity.id === entity.id)!;
    const menuRouteType = 'EditHeldAwayAccount';
    const menuOptions = [{
        itemText: 'Edit Held Away Account',
        value: 'Edit Held Away Account',
        route: `/Profile/${asset.profileId}/ClientProfile/${viewType}/${menuRouteType}/${asset.id}`,
    }];
    return {
        itemName: asset.financialAccountName,
        itemNameSubtext: getItemNameSubText(asset),
        itemValue: calculateValue(asset.marketEstateValue.totalValue, entityOwnership),
        menuOptions
    };
}


function mapBankingAccountToGroupTableItem(
    viewType: AssetsViewContextValue,
    asset: BankingAccountsSummary,
    entity: LegalEntityReadModel,
    getItemNameSubText: (bankingAccount: BankingAccountsSummary) => string = (bankingAccount) => formatAsOfDate(bankingAccount.valueAsOfDate)
): GroupedTableItem {

    const entityOwnership = asset.legalEntityOwnerships.find(ownership => ownership.legalEntity.id === entity.id)!;
    const menuRouteType = 'EditBankingAccount';
    const menuOptions = [{
        itemText: 'Edit Banking Account',
        value: 'Edit Banking Account',
        route: `/Profile/${asset.profileId}/ClientProfile/${viewType}/${menuRouteType}/${asset.id}`,
    }];
    return {
        itemName: asset.accountName,
        itemNameSubtext: getItemNameSubText(asset),
        itemValue: calculateValue(asset.eodNtBalance, entityOwnership),
        menuOptions
    };
}



function mapPartiallyOwnedInvestmentToGroupTableItem(viewType: AssetsViewContextValue,
                                           asset: PartiallyOwnedLegalAgreement,
                                           entity: LegalEntityReadModel): GroupedTableItem {

    const entityOwnership = asset.legalEntityOwnerships.find(ownership => ownership.legalEntity.id === entity.id)!;
    const menuOptions = [
        {
        itemText: 'Edit Account',
        value: asset.id,
        route: `/Profile/${asset.profileId}/ClientProfile/${viewType}/EditPartiallyOwnedInvestment/${asset.id}`
        }
    ];

    return {
        itemName: asset.legalAgreementName,
        itemValue: calculateValue(asset.marketEstateValue.totalValue, entityOwnership),
        menuOptions
    };
}

function formatAsOfDate(asOfDate?: string) {
    return asOfDate ? `As of ${moment(asOfDate).format(DISPLAY_DATE_FORMAT)}` : '--';
}

function byCustomOrder(a: LegalEntityTypeSummary, b: LegalEntityTypeSummary): number {
    const aOrdinal = legalEntityTypes.indexOf(a.entityType);
    const bOrdinal = legalEntityTypes.indexOf(b.entityType);
    return aOrdinal - bOrdinal;
}

function calculateValue(presentValue: number | null, ownership: { percentage: number }) {
    const value = presentValue ?? 0;
    return (value * ownership.percentage) / 100;
}

function getMenuOptions(viewType: AssetsViewContextValue,
                                   memberAsset: NonClientMemberAsset) {

    let menuText ="";

    if (memberAsset.assetType === 'HeldAwayAccount'){
         menuText = memberAsset.assetType === "HeldAwayAccount" ? 'Edit Held Away Account' : 'Edit Asset';
    }
    else if (memberAsset.assetType === 'PersonalAsset' || memberAsset.assetType === 'LifeInsurance'){
        menuText = 'Edit Asset';
    }
    else if (memberAsset.assetType === 'StandaloneAccount' || memberAsset.assetType === 'PartiallyOwnedInvestment'){
        menuText = 'Edit Account';
    }
    else if (memberAsset.assetType === 'BankingAccount'){
        menuText = memberAsset.assetType === "BankingAccount" ? 'Edit Banking Account' : 'Edit Asset';
    }
    else{
         menuText = 'Edit Liability';
    }
    return [{
        itemText: menuText,
        value: memberAsset.assetId,
        route: `/Profile/${memberAsset.profileId}/ClientProfile/${viewType}/Edit${memberAsset.assetType}/${memberAsset.assetId}`,
    }];
}
